import { PLATFORM_TYPE } from '../../model/oauth/type';
import React from 'react';
import { FORM_TYPE } from './PrescriptionNotes/type';
import { getWebAbsPath } from '../../util/path';
import InfoIcon from '@material-ui/icons/Info';
import Typography from '@material-ui/core/Typography';

import { Flare, Info, QuestionAnswer, Image, Brightness5, LocalShipping, InsertDriveFile } from '@material-ui/icons';
import _ from 'lodash';
import { BASE_PRE_IMPLANT_ALIGNMENT, BASE_RESTORATIVE_OPTIONS } from '../../store/NewPatient/NewPatient.reducer';
import { smileDesignProductListArray } from '../../store/SmileDesign/SmileDesignSlice';
import { infoIconColor } from '../DifferentRetainer/PrescriptionNote/RetainerTypeFormControlLabelRhf';
import CustomTooltip from '../DifferentRetainer/components/CustomTooltip/CustomTooltip';

/** Determines whether the Q14-Q16 upsell section is rendered for a prescription form. */
export const shouldShowUpsellForm = (allowedFeatures, formType = null) => {
    if (!allowedFeatures?.smile_design) return false;
    return ![FORM_TYPE.REFINEMENT, FORM_TYPE.ADMIN_UPLOAD_PRESCRIPTION_NOTES].includes(formType);
};

// this validations is for special handling of Eletra impression files (temp function)
export const validateImpressionFiles = (impressionFiles = []) => {
    let hasEletra = false;
    const impressionErrList = impressionFiles.filter((v) => {
        if (v.platform && [PLATFORM_TYPE.PandaCloud_EN, PLATFORM_TYPE.PandaCloud_EU].includes(v.platform)) {
            if (v.data && v.uploaded && v.id === 'impression_upper') {
                hasEletra = true;
            }
        }
        if (hasEletra) return false;
        return (
            !v.data &&
            !v.upload_later &&
            !v.will_not_upload &&
            !(v.id === 'impression_scan_two' && (v.data === null || v.data === ''))
        );
    });
    return impressionErrList;
};

export function getPrescriptionNotesQuestions(getNotes) {
    let questions = {
        archToTreat: {
            value: 'archToTreat',
            label: getNotes('archToTreat'),
            options: [
                { value: 'both', label: getNotes('both') },
                { value: 'archToTreatUpper', label: getNotes('archToTreatUpper') },
                { value: 'archToTreatLower', label: getNotes('archToTreatLower') },
            ],
        },
        excludedDentition: {
            value: 'excludedDentition',
            label: getNotes('excludedDentition'),
        },
        attachments: {
            value: 'attachments',
            label: getNotes('attachments'),
            options: [
                {
                    value: 'placeAttachmentsAsNeeded',
                    label: getNotes('placeAttachmentsAsNeeded'),
                    expandingGridLabel: getNotes('placeAttachmentsAsNeeded'),
                },
                {
                    value: 'includedExcludedAttachments',
                    label: getNotes('includedExcludedAttachments'),
                    option: {},
                    expandingGridLabel: getNotes('includedExcludedAttachments_v2'),
                },
                {
                    value: 'noAttachmentAtAll',
                    label: getNotes('noAttachmentAtAll'),
                    option: {},
                    helperText: getNotes('noAttachmentAtAll_helperText'),
                    expandingGridLabel: getNotes('noAttachmentAtAll'),
                },
            ],
        },
        attachmentsDelayStage: {
            value: 'delayStage',
            label: getNotes('delayStage'),
            options: [
                { value: 'noDelay', label: getNotes('noDelay'), expandingGridLabel: getNotes('noDelay') },
                {
                    value: 'delayToStage',
                    label: getNotes('delayToStage'),
                    expandingGridLabel: getNotes('delayToStage_v2'),
                },
            ],
            menu: [
                '--',
                '1',
                '2',
                '3',
                '4',
                '5',
                '6',
                '7',
                '8',
                '9',
                '10',
                '11',
                '12',
                '13',
                '14',
                '15',
                '16',
                '17',
                '18',
                '19',
                '20',
                '21',
                '22',
                '23',
                '24',
                '25',
                '26',
                '27',
                '28',
                '29',
                '30',
            ],
        },
        alignersMarginPlacement: {
            value: 'alignersMarginPlacement',
            label: getNotes('alignersMarginPlacement'),
            options: [
                { value: 'coverageExtending', label: getNotes('coverageExtending') },
                { value: 'scallopedAlignerMargins', label: getNotes('scallopedAlignerMargins') },
            ],
        },
        anteriorPosteriorRelationship: {
            value: 'anteriorPosteriorRelationship',
            label: getNotes('anteriorPosteriorRelationship'),
            options: [
                {
                    value: 'maintain',
                    label: getNotes('maintain'),
                },
                {
                    value: 'improveCanine',
                    label: getNotes('improveCanine'),
                },
                {
                    value: 'improveCanineAndMolar',
                    label: getNotes('improveCanineAndMolar'),
                },
                {
                    value: 'correctionToClassI',
                    label: getNotes('correctionToClassI'),
                },
            ],
        },
        spacingAndCrowding: {
            value: 'spacingAndCrowding',
            label: getNotes('spacingAndCrowding'),
            options: [
                {
                    value: 'spacing',
                    label: getNotes('spacing'),
                    options: [
                        {
                            value: 'closeAllSpaces',
                            label: getNotes('closeAllSpaces'),
                            expandingGridLabel: getNotes('closeAllSpaces'),
                        },
                        {
                            value: 'leaveSpecificSpaces',
                            label: getNotes('leaveSpecificSpaces'),
                            expandingGridLabel: getNotes('leaveSpecificSpaces_v2'),
                        },
                    ],
                },
                {
                    value: 'crowding',
                    label: getNotes('crowding'),
                    options: [
                        {
                            value: 'resolveUpper',
                            label: getNotes('resolveUpper'),
                            options: [
                                { value: 'expand', label: getNotes('expand') },
                                { value: 'procline', label: getNotes('procline') },
                                { value: 'IPRAnterior', label: getNotes('IPRAnterior') },
                                { value: 'IPRPosteriorRight', label: getNotes('IPRPosteriorRight') },
                                { value: 'IPRPosteriorLeft', label: getNotes('IPRPosteriorLeft') },
                                { value: 'extraction', label: getNotes('extraction') },
                            ],
                        },
                        {
                            value: 'resolveLower',
                            label: getNotes('resolveLower'),
                            options: [
                                { value: 'expand', label: getNotes('expand') },
                                { value: 'procline', label: getNotes('procline') },
                                { value: 'IPRAnterior', label: getNotes('IPRAnterior') },
                                { value: 'IPRPosteriorRight', label: getNotes('IPRPosteriorRight') },
                                { value: 'IPRPosteriorLeft', label: getNotes('IPRPosteriorLeft') },
                                { value: 'extraction', label: getNotes('extraction') },
                            ],
                        },
                    ],
                },
            ],
        },
        overjet: {
            value: 'overjet',
            label: getNotes('overjet'),
            options: [
                { value: 'maintainInitialOverjet', label: getNotes('maintainInitialOverjet') },
                {
                    value: 'improveResultingOverjetBy',
                    label: getNotes('improveResultingOverjetBy'),
                    options: [
                        {
                            value: 'noPreferenceShowResultsInSimulator',
                            label: getNotes('noPreferenceShowResultsInSimulator'),
                        },
                        { value: 'IPRANDRetraction', label: getNotes('IPRANDRetraction') },
                        {
                            value: 'transverseExpansionAndRetraction',
                            label: getNotes('transverseExpansionAndRetraction'),
                        },
                        {
                            value: 'combinationOfIPRAndTransverseExpansion',
                            label: getNotes('combinationOfIPRAndTransverseExpansion'),
                        },
                    ],
                },
            ],
        },
        openbite: {
            value: 'openbite',
            label: getNotes('openbite'),
            options: [
                { value: 'maintainInitialOpenbite', label: getNotes('maintainInitialOpenbite') },
                {
                    value: 'improveResultingOpenbiteBy',
                    label: getNotes('improveResultingOpenbiteBy'),
                    options: [
                        {
                            value: 'noPreferenceShowResultsInSimulator',
                            label: getNotes('noPreferenceShowResultsInSimulation'),
                        },
                        {
                            value: 'extrudeAnteriorTeeth',
                            label: getNotes('extrudeAnteriorTeeth'),
                            options: [
                                { value: 'upper', label: getNotes('upper') },
                                { value: 'lower', label: getNotes('lower') },
                            ],
                        },
                        {
                            value: 'intrudePosteriorTeeth',
                            label: getNotes('intrudePosteriorTeeth'),
                            options: [
                                { value: 'upper', label: getNotes('upper') },
                                { value: 'lower', label: getNotes('lower') },
                            ],
                        },
                        {
                            value: 'placeMiniImplantsTAD',
                            label: getNotes('placeMiniImplantsTAD'),
                            expandingGridLabel: getNotes('placeMiniImplantsTAD_v2'),
                        },
                    ],
                },
            ],
        },
        deepBite: {
            value: 'deepBite',
            label: getNotes('deepBite'),
            options: [
                { value: 'maintainExistingDeepBite', label: getNotes('maintainExistingDeepBite') },
                {
                    value: 'improveResultingDeepBiteBy',
                    label: getNotes('improveResultingDeepBiteBy'),
                    options: [
                        { value: 'noPreference', label: getNotes('noPreferenceShowResultsInSimulation') },
                        {
                            value: 'intrudeAnteriorTeeth',
                            label: getNotes('intrudeAnteriorTeeth'),
                            options: [
                                { value: 'upper', label: getNotes('upper') },
                                { value: 'lower', label: getNotes('lower') },
                            ],
                        },
                        {
                            value: 'extrudePosteriorTeeth',
                            label: getNotes('extrudePosteriorTeeth'),
                            options: [
                                { value: 'upper', label: getNotes('upper') },
                                { value: 'lower', label: getNotes('lower') },
                            ],
                        },
                        {
                            value: 'placeMiniImplantsTAD',
                            label: getNotes('placeMiniImplantsTAD'),
                            expandingGridLabel: getNotes('placeMiniImplantsTAD_v2'),
                        },
                    ],
                },
            ],
        },
        midline: {
            value: 'midline',
            label: getNotes('midline'),
            options: [
                { value: 'maintainInitialMidline', label: getNotes('maintainInitialMidline') },
                {
                    value: 'improveResultingMidlineBy',
                    label: getNotes('improveResultingMidlineBy'),
                    options: [
                        { value: 'noPreference', label: getNotes('noPreferenceShowResultsInSimulation') },
                        {
                            value: 'upper',
                            label: getNotes('upper'),
                            options: [
                                { value: 'toPatientsRight', label: getNotes('toPatientsRight') },
                                { value: 'toPatientsLeft', label: getNotes('toPatientsLeft') },
                            ],
                        },
                        {
                            value: 'lower',
                            label: getNotes('lower'),
                            options: [
                                { value: 'toPatientsRight', label: getNotes('toPatientsRight') },
                                { value: 'toPatientsLeft', label: getNotes('toPatientsLeft') },
                            ],
                        },
                    ],
                },
            ],
        },
        posteriorCrossbite: {
            value: 'posteriorCrossbite',
            label: getNotes('posteriorCrossbite'),
            options: [
                {
                    value: 'maintainInitialCrossbite',
                    label: getNotes('maintainInitialCrossbite'),
                    expandingGridLabel: getNotes('maintainInitialCrossbite'),
                },
                {
                    value: 'correct',
                    label: getNotes('correct'),
                    expandingGridLabel: getNotes('correct_v2'),
                    options: [
                        {
                            value: 'noPreferenceShowResultsInSimulator',
                            label: getNotes('noPreferenceShowResultsInSimulator'),
                        },
                        {
                            value: 'interarchElastics',
                            label: getNotes('interarchElastics'),
                            options: [
                                {
                                    value: 'cutoutToBondButtonDirectlyOnTeeth',
                                    label: getNotes('cutoutToBondButtonDirectlyOnTeeth'),
                                    options: [
                                        { value: 'maxillaryTeeth', label: getNotes('maxillaryTeeth') },
                                        { value: 'mandibularTeeth', label: getNotes('mandibularTeeth') },
                                    ],
                                },
                                {
                                    value: 'buttonPressedDirectlyOnAligner',
                                    label: getNotes('buttonPressedDirectlyOnAligner'),
                                    options: [
                                        { value: 'maxillaryTeeth', label: getNotes('maxillaryTeeth') },
                                        { value: 'mandibularTeeth', label: getNotes('mandibularTeeth') },
                                    ],
                                },
                            ],
                        },
                        {
                            value: 'noElastics',
                            label: getNotes('noElastics'),
                            expandingGridLabel: getNotes('noElastics'),
                        },
                        {
                            value: 'otherMethods',
                            label: getNotes('otherMethods'),
                            expandingGridLabel: getNotes('otherMethods'),
                        },
                    ],
                },
            ],
        },
        cuttingOptions: {
            value: 'cuttingOptions',
            label: getNotes('cuttingOptions'),
            options: [
                { value: 'gingivalCutting', label: getNotes('gingivalCutting') },
                { value: 'scallopCutting', label: getNotes('scallopCutting') },
                { value: 'none', label: getNotes('none') },
            ],
        },
        auxiliaries: {
            value: 'auxiliaries',
            label: getNotes('auxiliaries'),
            options: [
                {
                    value: 'elastics',
                    label: getNotes('elastics'),
                    options: [
                        { value: 'classIIElastics', label: getNotes('classIIElastics') },
                        { value: 'classIIIElastics', label: getNotes('classIIIElastics') },
                        {
                            value: 'elasticEngagementPreference',
                            label: getNotes('elasticEngagementPreference'),
                            options: [
                                {
                                    value: 'upper',
                                    label: getNotes('upperDentition'),
                                    options: [
                                        {
                                            value: 'upperFabricate',
                                            label: getNotes('fabricateButtonDirectlyOnAligners'),
                                        },
                                        { value: 'upperCutout', label: getNotes('cutoutOnAligner') },
                                        { value: 'upperSlipCut', label: getNotes('slipCutPrecisionCut') },
                                    ],
                                },
                                {
                                    value: 'lower',
                                    label: getNotes('lowerDentition'),
                                    options: [
                                        {
                                            value: 'lowerFabricate',
                                            label: getNotes('fabricateButtonDirectlyOnAligners'),
                                        },
                                        { value: 'lowerCutout', label: getNotes('cutoutOnAligner') },
                                        { value: 'lowerSlipCut', label: getNotes('slipCutPrecisionCut') },
                                    ],
                                },
                            ],
                        },
                        {
                            value: 'otherElastics',
                            label: getNotes('otherElastics'),
                            options: [
                                { value: 'Yes', label: getNotes('yes') },
                                { value: 'No', label: getNotes('no') },
                            ],
                        },
                    ],
                },
            ],
        },
        trioDimForce: {
            value: 'trioDimForce',
            label: getNotes('trioDimForcePreference'),
            options: [
                { value: 'true', label: getNotes('trioDimForce_true') },
                { value: 'false', label: getNotes('trioDimForce_false') },
            ],
        },
        restorativeOptions: {
            value: 'restorativeOptions',
            label: getNotes('restorativeOptions'),
            options: [
                { value: 'No', label: getNotes('no') },
                {
                    value: 'Yes',
                    // label: getNotes('yes'),
                    label: getCustomFlowableInjectionLabel(getNotes),
                    // options: [
                    //     {
                    //         value: 'flowableInjection',
                    //         // label: getNotes('flowableInjection')
                    //         label: getCustomFlowableInjectionLabel(getNotes),
                    //     },
                    //     { value: 'compositeOnlay', label: getNotes('compositeOnlay') },
                    // ],
                },
            ],
            verticalDimension: {
                // verticalDimensionToothArea: {
                //     value: 'verticalDimensionToothArea',
                //     label: '',
                //     options: [
                //         { value: 'anterior', label: getNotes('anterior') },
                //         { value: 'posterior', label: getNotes('posterior') },
                //     ],
                // },
                // verticalDimensionJawPosition: {
                //     value: 'verticalDimensionJawPosition',
                //     label: '',
                //     options: [
                //         { value: 'upper', label: getNotes('upper') },
                //         { value: 'lower', label: getNotes('lower') },
                //         { value: 'upperAndLower', label: getNotes('upperAndLower') },
                //     ],
                // },
                // verticalDimensionIncrease: {
                //     value: 'verticalDimensionIncrease',
                //     label: '',
                //     options: [
                //         { value: '2mm', label: '2mm' },
                //         { value: '3mm', label: '3mm' },
                //         { value: '4mm', label: '4mm' },
                //         { value: '5mm', label: '5mm' },
                //         { value: 'NoPreference', label: getNotes('noPreference') },
                //     ],
                // },
                verticalDimensionUpperPosterior: {
                    value: 'verticalDimensionUpperPosterior',
                    label: getNotes('upperPosterior'),
                    options: [
                        { value: '', label: 'None', disabled: false },
                        { value: '1mm', label: '1mm', disabled: false },
                        { value: '2mm', label: '2mm', disabled: false },
                        { value: '3mm', label: '3mm', disabled: false },
                        { value: '4mm', label: '4mm', disabled: false },
                        { value: '5mm', label: '5mm', disabled: false },
                        // { value: 'NoPreference', label: getNotes('noPreference') },
                    ],
                },
                verticalDimensionLowerPosterior: {
                    value: 'verticalDimensionLowerPosterior',
                    label: getNotes('lowerPosterior'),
                    options: [
                        { value: '', label: 'None', disabled: false },
                        { value: '1mm', label: '1mm', disabled: false },
                        { value: '2mm', label: '2mm', disabled: false },
                        { value: '3mm', label: '3mm', disabled: false },
                        { value: '4mm', label: '4mm', disabled: false },
                        { value: '5mm', label: '5mm', disabled: false },
                        // { value: 'NoPreference', label: getNotes('noPreference') },
                    ],
                },
            },
        },
        preImplantAligment: {
            value: 'preImplantAligment',
            label: getNotes('preImplantAligment'),
            options: [
                { value: 'No', label: getNotes('no') },
                { value: 'Yes', label: getNotes('yes') },
            ],
        },
        addOnSmileDesign: {
            value: 'addOnSmileDesign',
            label: getNotes('addOnSmileDesign'),
            options: [
                { value: 'No', label: getNotes('no') },
                { value: 'Yes', label: getNotes('yes') },
            ],
        },
        additionalInstructionsOrSpecialRequests: {
            value: 'additionalInstructionsOrSpecialRequests',
            label: getNotes('additionalInstructionsOrSpecialRequests'),
        },
    };
    return questions;
}

/**
 * Renders tooth position markers based on selected tooth values
 * @param {Array} selectedTeeth - Array of selected tooth values
 * @param {Object} teeth - Teeth data structure with position information
 * @param {string} imageSrc - Path to the marker image
 * @returns {Array} Array of JSX img elements
 */
export const renderToothMarkers = (selectedTeeth, teeth, imageSrc = '/img/prescriptionNotes/checkSuccess.png') => {
    if (!Array.isArray(selectedTeeth) || !teeth) return [];
    return selectedTeeth
        .map((toothValue, index) => {
            for (let [_, objValue] of Object.entries(teeth)) {
                for (let { value, positionX, positionY } of objValue.values) {
                    if (value === toothValue) {
                        return (
                            <img
                                key={`tooth-marker-${toothValue}-${index}`}
                                src={getWebAbsPath(imageSrc)}
                                alt="checked"
                                style={{
                                    position: 'absolute',
                                    top: positionY - 10,
                                    left: positionX - 12,
                                    userSelect: 'none',
                                }}
                            />
                        );
                    }
                }
            }
            return null; // Return null if tooth not found
        })
        .filter(Boolean); // Remove null values
};

export const WORKFLOW_TAB = {
    INFO: 'info',
    PRESCRIPTION_NOTES: 'prescriptionNotes',
    PHOTOS: 'photos',
    RADIOGRAPHY: 'radiography',
    IMPRESSION: 'impression',
    DELIVERY: 'delivery',
    SUMMARY: 'summary',
    RETAINER_REQUEST_PRESCRIPTION_NOTES: 'retainer_request_prescriptionNotes',
    PRESCRIPTION_NOTES_UPSELL_SMILE_DESIGN: 'prescriptionNotesSD',
};

export const ALIGNER_WORKFLOW_TABS = [
    {
        tab: WORKFLOW_TAB.INFO,
        name: 'patient_info', // for errors in the store
        lang: '@NewPatient.tabs.info',
        icon: Info,
        isBaseOption: true,
    },
    {
        tab: WORKFLOW_TAB.PRESCRIPTION_NOTES,
        name: 'prescriptionNotes',
        lang: '@NewPatient.tabs.trioclearPrescriptionNotes',
        icon: QuestionAnswer,
        isBaseOption: true,
    },
    {
        tab: WORKFLOW_TAB.PRESCRIPTION_NOTES_UPSELL_SMILE_DESIGN,
        name: WORKFLOW_TAB.PRESCRIPTION_NOTES_UPSELL_SMILE_DESIGN,
        lang: '@NewPatient.tabs.ISDPrescriptionNotes',
        icon: QuestionAnswer,
        isBaseOption: false,
    },
    {
        tab: WORKFLOW_TAB.PHOTOS,
        name: 'photo',
        lang: '@global.fields.photos',
        icon: Image,
        isBaseOption: true,
    },
    {
        tab: WORKFLOW_TAB.RADIOGRAPHY,
        name: 'radiograph',
        lang: '@global.fields.radiographs',
        icon: Flare,
        isBaseOption: true,
    },
    {
        tab: WORKFLOW_TAB.IMPRESSION,
        name: 'impression',
        lang: '@global.fields.impression',
        icon: Brightness5,
        isBaseOption: true,
    },
    {
        tab: WORKFLOW_TAB.DELIVERY,
        name: 'delivery',
        lang: '@NewPatient.tabs.delivery',
        icon: LocalShipping,
        isBaseOption: true,
    },
    {
        tab: WORKFLOW_TAB.SUMMARY,
        name: 'summary',
        lang: '@NewPatient.tabs.summary',
        icon: InsertDriveFile,
        isBaseOption: true,
    },
];

export const getBaseAlignerWorkFlowTabs = () => {
    return ALIGNER_WORKFLOW_TABS.filter((t) => t.isBaseOption);
};

export const getPrescriptionNotesTabLang = (newCaseType) => {
    if (smileDesignProductListArray.includes(newCaseType)) {
        return '@NewPatient.tabs.ISDPrescriptionNotes';
    }
    return '@NewPatient.tabs.trioclearPrescriptionNotes';
};

export const RETAINER_WORKFLOW_TABS = [
    {
        tab: WORKFLOW_TAB.INFO,
        name: 'patient_info',
        lang: '@NewPatient.tabs.info',
        icon: Info,
    },
    {
        tab: WORKFLOW_TAB.RETAINER_REQUEST_PRESCRIPTION_NOTES,
        name: 'retainer_request_prescription',
        lang: '@NewPatient.tabs.prescriptionNotes',
        icon: QuestionAnswer,
    },
    {
        tab: WORKFLOW_TAB.IMPRESSION,
        name: 'impression',
        lang: '@global.fields.impression',
        icon: Brightness5,
    },
    {
        tab: WORKFLOW_TAB.DELIVERY,
        name: 'delivery',
        lang: '@NewPatient.tabs.delivery',
        icon: LocalShipping,
    },
    {
        tab: WORKFLOW_TAB.SUMMARY,
        name: 'summary',
        lang: '@NewPatient.tabs.summary',
        icon: InsertDriveFile,
    },
];

export const SELF_DESIGN_WORKFLOW_TABS = [
    {
        tab: WORKFLOW_TAB.INFO,
        name: 'patient_info',
        lang: '@NewPatient.tabs.info',
        icon: Info,
    },
    {
        tab: WORKFLOW_TAB.DELIVERY,
        name: 'delivery',
        lang: '@NewPatient.tabs.delivery',
        icon: LocalShipping,
    },
    {
        tab: WORKFLOW_TAB.SUMMARY,
        name: 'summary',
        lang: '@NewPatient.tabs.summary',
        icon: InsertDriveFile,
    },
];

export const handleRestorativeOptions = ({ modifiedKey, value }) => {
    let res = {
        restorativeOptions: null,
        onlayPosition: null,
        verticalDimensionUpperPosterior: null,
        verticalDimensionLowerPosterior: null,
    };
    if (modifiedKey !== 'restorative_options') return res;
    const { subValue } = value;
    if (subValue?.value) {
        res.restorativeOptions = subValue.value;
    }
    const { onlayPosition, verticalDimension } = subValue || {};
    if (Array.isArray(onlayPosition) && onlayPosition.length > 0) {
        res.onlayPosition = JSON.stringify(onlayPosition);
    }
    if (verticalDimension) {
        const { verticalDimensionUpperPosterior, verticalDimensionLowerPosterior } = verticalDimension;
        res.verticalDimensionUpperPosterior = verticalDimensionUpperPosterior || null;
        res.verticalDimensionLowerPosterior = verticalDimensionLowerPosterior || null;
    }
    return res;
};

export const handlePreImplantAligment = ({ modifiedKey, value }) => {
    let res = null;
    if (modifiedKey !== 'pre_implant_aligment') return res;
    const { prostheticSpace } = value;
    if (Array.isArray(prostheticSpace) && prostheticSpace.length > 0) {
        res = JSON.stringify(prostheticSpace);
    }
    return res;
};
export const handleSetRestorativeOptionsData = (caseDetail) => {
    let baseRestorativeOptionsObj = _.cloneDeep(BASE_RESTORATIVE_OPTIONS);
    if (!caseDetail || !caseDetail?.restorative_options) return baseRestorativeOptionsObj;
    baseRestorativeOptionsObj.value = 'Yes';
    baseRestorativeOptionsObj.subValue.value = caseDetail?.restorative_options;
    if (caseDetail?.onlay_position && Array.isArray(caseDetail?.onlay_position)) {
        baseRestorativeOptionsObj.subValue.onlayPosition = [...caseDetail?.onlay_position];
    }
    const { vertical_dimension_upper_posterior, vertical_dimension_lower_posterior } = caseDetail;
    baseRestorativeOptionsObj.subValue.verticalDimension.verticalDimensionUpperPosterior =
        vertical_dimension_upper_posterior ?? '';
    baseRestorativeOptionsObj.subValue.verticalDimension.verticalDimensionLowerPosterior =
        vertical_dimension_lower_posterior ?? '';
    return baseRestorativeOptionsObj;
};

export const handleSetPreImplantAligmentData = (caseDetail) => {
    let basePreImplantAligmentObj = _.cloneDeep(BASE_PRE_IMPLANT_ALIGNMENT);
    if (!caseDetail || !caseDetail?.prosthetic_space) return basePreImplantAligmentObj;
    if (Array.isArray(caseDetail?.prosthetic_space) && caseDetail?.prosthetic_space.length > 0) {
        basePreImplantAligmentObj.value = 'Yes';
        basePreImplantAligmentObj.prostheticSpace = [...caseDetail?.prosthetic_space];
    }
    return basePreImplantAligmentObj;
};

export const handleSetAddOnSmileDesignValue = (caseSmileDesignData) => {
    return caseSmileDesignData ? 'Yes' : 'No';
};

export const handleCleanUpAddOnSmileDesignSubValue = (submitData) => {
    let res = _.cloneDeep(submitData);
    if (res?.verticalDimension === 'option.notIncreaseVerticalDimension' || !res?.verticalDimension) {
        res['verticalDimensionUpperPosterior'] = null;
        res['verticalDimensionLowerPosterior'] = null;
    }
    if (res['preRestorativeAlignerSimulation'] === 'option.no') {
        res['upgradePreRestorativeAlignerSimulation'] = null;
    }
    return res;
};

export const extractSmileDesignFormDataForUpsell = (case_smile_design) => {
    if (!case_smile_design) return null;
    const { isMarieGroup, ...formData } = case_smile_design;
    return formData;
};

export const getBaseEmptySmileDesignFormData = (isMarieGroup = false) => {
    return {
        isMarieGroup,
        plannedRestorationSolution: '',
        teethSD: [],
        teethForSimulation: [],
        maxillaryMidlinePosition: '',
        mandibularMidlineRelationship: '',
        maxillaryMidlineAngulation: '',
        midlineCorrectionInSimulation: '',
        cantingInSimulation: '',
        teethReductionPreference: [],
        buccalFacial_teethReduction: '',
        palatalLingual_teethReduction: '',
        teethNotChange: [],
        verticalDimension: '',
        verticalDimensionUpperPosterior: '2mm',
        verticalDimensionLowerPosterior: '2mm',
        preRestorativeAlignerSimulation: '',
        upgradePreRestorativeAlignerSimulation: '',
        remark: '',
    };
};

export const proccessUpsellSmileDesignDataFromServerToStore = (formData) => {
    let cloned = _.cloneDeep(formData);
    // Set default values for posterior fields if empty
    if (!cloned.verticalDimensionUpperPosterior) {
        cloned['verticalDimensionUpperPosterior'] = '2mm';
    }
    if (!cloned.verticalDimensionLowerPosterior) {
        cloned['verticalDimensionLowerPosterior'] = '2mm';
    }
    return cloned;
};

export function getCustomFlowableInjectionLabel(getNotes) {
    if (!getNotes)
        return 'Yes. Flowable Injection (If a TrioClear treatment will be carried out, you will receive the F.I.T. package with the first batch of aligners at the same time.)';
    return (
        <>
            <Typography component="span" variant="body1">
                <>{getNotes('yes')}</>
                <>{' - '}</>
                <> {getNotes('flowableInjection_short')}</>
                <CustomTooltip
                    placement="right"
                    title={getNotes('flowableInjection_tooltip_title')}
                    description={getNotes('flowableInjection_tooltip_content')}
                    imgSrc={getWebAbsPath('/img/flowable_injection.jpg')}
                    imgStyle={{
                        height: 220,
                        width: 380,
                        borderRadius: 8, // added
                        objectFit: 'cover', // optional: keeps aspect and fills box,
                    }}
                >
                    <InfoIcon
                        style={{ verticalAlign: 'middle', marginLeft: 4, marginRight: 4 }}
                        color={infoIconColor}
                    />
                </CustomTooltip>
                <>{getNotes('flowableInjection_info')}</>
            </Typography>
        </>
    );
}
